import type z from 'zod/v4'
import { relations, sql } from 'drizzle-orm'
import { boolean, index, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { organization, user } from '../auth'
import { LessonModule } from '../content'
import { LearnEnrollment } from './learn-enrollment'

export const Course = pgTable(
  'course',
  {
    id: uuid().primaryKey().defaultRandom(),
    userId: text()
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),
    // Teacher who first assigned this course. NULL means never reassigned — `userId` is still the
    // original. Set once, on the first handover, so repeat handovers keep pointing at the first
    // teacher. `set null` (not cascade): losing that account must not delete the course.
    originalUserId: text().references(() => user.id, { onDelete: 'set null' }),
    organizationId: text()
      .notNull()
      .references(() => organization.id, { onDelete: 'cascade' }),
    lessonModuleId: uuid()
      .notNull()
      .references(() => LessonModule.id, { onDelete: 'cascade' }),
    title: text().notNull(),
    description: text(),
    startDate: timestamp({ mode: 'date', withTimezone: true }),
    endDate: timestamp({ mode: 'date', withTimezone: true }),
    isActive: boolean().notNull().default(true),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (t) => [
    index('idx_course_user').on(t.userId),
    index('idx_course_organization').on(t.organizationId),
    index('idx_course_lesson_module').on(t.lessonModuleId),
  ],
)

export const courseRelations = relations(Course, ({ one, many }) => ({
  user: one(user, {
    fields: [Course.userId],
    references: [user.id],
  }),

  originalUser: one(user, {
    fields: [Course.originalUserId],
    references: [user.id],
  }),

  organization: one(organization, {
    fields: [Course.organizationId],
    references: [organization.id],
  }),

  lessonModule: one(LessonModule, {
    fields: [Course.lessonModuleId],
    references: [LessonModule.id],
  }),

  learnEnrollments: many(LearnEnrollment),
}))

export const InsertCourseSchema = createInsertSchema(Course)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    originalUserId: true,
  })
  .superRefine((data, ctx) => {
    // Validate that endDate is after startDate
    if (data.endDate && data.startDate && data.endDate <= data.startDate) {
      ctx.addIssue({
        code: 'custom',
        message: 'End date must be after start date',
        path: ['endDate'],
      })
    }
  })

export const UpdateCourseSchema = createUpdateSchema(Course)
  .partial()
  .omit({
    id: true,
    userId: true,
    organizationId: true,
    lessonModuleId: true,
    createdAt: true,
    updatedAt: true,
    originalUserId: true,
  })
  .superRefine((data, ctx) => {
    // Validate that endDate is after startDate
    if (data.endDate && data.startDate && data.endDate <= data.startDate) {
      ctx.addIssue({
        code: 'custom',
        message: 'End date must be after start date',
        path: ['endDate'],
      })
    }
  })

export const SelectCourseSchema = createSelectSchema(Course)

export type InsertCourse = typeof Course.$inferInsert
export type UpdateCourse = z.infer<typeof UpdateCourseSchema>
export type SelectCourse = typeof Course.$inferSelect
